import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, ActivityIndicator, KeyboardAvoidingView, Platform, Image } from 'react-native';
import useAuthStore from '@messenger/stores/authStore';
import useMessengerStore from '@messenger/stores/messengerStore';
import messengerSocket from '@messenger/services/socketService';
import { API_BASE_URL } from '@messenger/config/api';
import { getMainAppUrl } from '../config/apiConfig';
import { openExternalUrl } from '../utils/openExternalUrl';

import logger from '../utils/logger';
import { colors } from '../theme/colors';
// Quick Messages catalog. Only "introduction" is shipped today; future
// categories (interest, more-info, next-steps, follow-up, decline) will be
// added back here once their behaviors are designed.
const QUICK_MESSAGE_CATEGORIES = [
  { id: 'introduction', label: 'Introduction', icon: '🙋', enabled: true },
];

// Emoji reactions — must stay in sync with ALLOWED_REACTIONS in
// fastapi_backend/services/messenger_service.py.
const REACTION_EMOJIS = ['❤️', '👍', '😂', '😮', '🙏', '😢'];

// Helper: calc age from a dob string OR a (year, month) pair. The main app
// stores birth as separate `birthMonth` / `birthYear` fields (no day), so we
// approximate the age from year+month (1st of month).
const calcAge = (dob, birthYear, birthMonth) => {
  let d = null;
  if (dob) {
    const parsed = new Date(dob);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  } else if (birthYear) {
    const m = Number(birthMonth) || 1;
    const y = Number(birthYear);
    if (y > 1900 && y < 2200) d = new Date(Date.UTC(y, m - 1, 1));
  }
  if (!d) return null;
  const diff = Date.now() - d.getTime();
  const age = new Date(diff).getUTCFullYear() - 1970;
  return age > 0 && age < 130 ? age : null;
};

// Build an authenticated URL for a profile image path. Mirrors the helper in
// ConversationListScreen so the card renders the same picture as the sidebar.
const buildImageUrl = (path, size) => {
  if (!path) return null;
  if (typeof path !== 'string') return null;
  const normalized = !path.startsWith('http') && !path.startsWith('/') ? `/${path}` : path;
  const token = useAuthStore.getState().token;
  let fullUrl = normalized.startsWith('http') ? normalized : `${API_BASE_URL}${normalized}`;
  // Strip any pre-baked token so we always inject the CURRENT one. Legacy
  // snapshots persisted the JWT in the URL → it would expire ~30 min later.
  if (fullUrl.includes('token=')) {
    fullUrl = fullUrl
      .replace(/([?&])token=[^&]*&?/, '$1')
      .replace(/[?&]$/, '');
  }
  // Derived variants (thumb/medium) — only on the protected media endpoint;
  // ignored by older backends, and never appended to signed/other URLs.
  if ((size === 'thumb' || size === 'medium') && fullUrl.includes('/api/users/media/')) {
    const sep = fullUrl.includes('?') ? '&' : '?';
    fullUrl = `${fullUrl}${sep}size=${size}`;
  }
  if (token) {
    const sep = fullUrl.includes('?') ? '&' : '?';
    return `${fullUrl}${sep}token=${encodeURIComponent(token)}`;
  }
  return fullUrl;
};

// Gender helpers (module level — avoids rebuilding these arrays per render).
const _GENDER_MALE = ['male', 'm', 'man'];
const _GENDER_FEMALE = ['female', 'f', 'woman'];
const _normGender = (g) => (g ? String(g).trim().toLowerCase() : '');
const genderPillLabel = (g) => {
  const n = _normGender(g);
  if (!n) return '❓ Unknown';
  if (_GENDER_MALE.includes(n)) return '👨 Male';
  if (_GENDER_FEMALE.includes(n)) return '👩 Female';
  return `⚧ ${String(g).trim()}`;
};
// The ⋮ menu (favorite/message actions) shows when the viewer is the opposite
// gender — or when either side's gender is unknown (can't determine).
const shouldShowCardMenu = (viewerGender, cardGender) => {
  const v = _normGender(viewerGender);
  const c = _normGender(cardGender);
  if (!v || !c) return true;
  return (_GENDER_MALE.includes(v) && _GENDER_FEMALE.includes(c)) ||
         (_GENDER_FEMALE.includes(v) && _GENDER_MALE.includes(c));
};

// Rich profile-card renderer for contentType="profile_card" messages.
// Mirrors the layout in Image 1 of the spec: avatar at top, name + meta pills,
// then Education History, Work Experience, and a free-form "looking for" blurb.
// `card` is the immutable snapshot persisted on the message document.
// Memoized — cards re-render only when their own props change, not on every
// storeMessages update (new message, read receipt, typing indicator).
const ProfileCard = React.memo(function ProfileCard({ card, isOwn, onUsernameClick, onMenuOpen, isFavorited, currentUserGender }) {
  // Memoized so the <Image> sees a stable uri across renders — rebuilding the
  // token-appended URL each render would look like a source change.
  const avatarUri = useMemo(
    () => (card?.avatarUrl ? buildImageUrl(String(card.avatarUrl), 'medium') : null),
    [card?.avatarUrl]
  );
  if (!card) return null;
  // Pills mirror the main-app card: age · DOB (MM/YYYY) · height. Prefer the
  // pre-formatted `dobLabel` (built from birthMonth/birthYear) and only fall
  // back to a runtime format if an ISO `dob` string is present.
  let dobPill = null;
  if (card.dobLabel) {
    dobPill = `📅 ${card.dobLabel}`;
  } else if (card.dob) {
    const parsed = new Date(card.dob);
    if (!Number.isNaN(parsed.getTime())) {
      dobPill = `📅 ${parsed.toLocaleDateString('en-US', { month: '2-digit', year: 'numeric' })}`;
    }
  }
  const genderPill = genderPillLabel(card.sex || card.gender);
  const rawDisplayName = String(card.fullName || card.username || 'Unknown').trim();
  const displayName = rawDisplayName.split(/\s+/)[0] || rawDisplayName;
  // One compact meta pill: dob · age · height · location — saves a full pill
  // row so more of the profile photo stays visible. `education`/`occupation`
  // stay out (duplicated by the sections below; no flat top-level fields).
  const combinedMeta = [
    genderPill !== '❓ Unknown' ? genderPill : null,
    dobPill,
    card.age ? `👋 ${card.age} yrs` : null,
    card.height ? `📏 ${card.height}` : null,
    card.location ? `📍 ${card.location}` : null,
  ].filter(Boolean).join(' · ');
  const showMenu = !isOwn && !!onMenuOpen && shouldShowCardMenu(currentUserGender, card.gender || card.sex);
  const hasEdu = Array.isArray(card.educationHistory) && card.educationHistory.length > 0;
  const hasWork = Array.isArray(card.workExperience) && card.workExperience.length > 0;
  return (
    <View style={[cardStyles.card, isOwn && cardStyles.cardOwn]}>
      <View style={cardStyles.cardInner}>
        {avatarUri ? (
          <>
            <Image source={{ uri: avatarUri }} style={cardStyles.cardInnerBgImage} resizeMode="cover" blurRadius={22} />
            <View style={cardStyles.cardInnerBgTint} />
          </>
        ) : null}

        <View style={cardStyles.cardContent}>
          <View style={cardStyles.heroImageWrap}>
            {avatarUri ? (
              <Image source={{ uri: avatarUri }} style={cardStyles.heroPhoto} resizeMode="contain" />
            ) : (
              <View style={[cardStyles.avatar, cardStyles.avatarFallback]}>
                <Text style={cardStyles.avatarFallbackText}>
                  {rawDisplayName.charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            <View style={cardStyles.heroOverlay}>
              <View style={cardStyles.headerRow}>
                <View style={cardStyles.nameBlock}>
                  <TouchableOpacity
                    style={cardStyles.nameTouchable}
                    onPress={() => card.username && onUsernameClick && onUsernameClick(card.username)}
                    activeOpacity={card.username ? 0.7 : 1}
                  >
                    <Text style={cardStyles.name} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.72}>
                      {displayName}
                    </Text>
                  </TouchableOpacity>
                </View>

                <View style={cardStyles.headerActions}>
                  {isFavorited && <Text style={cardStyles.heartEmoji}>❤️</Text>}
                  {showMenu && (
                    <TouchableOpacity
                      style={cardStyles.menuButton}
                      onPress={() => onMenuOpen(card)}
                      activeOpacity={0.7}
                    >
                      <Text style={cardStyles.menuButtonText}>⋮</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>

              {combinedMeta ? (
                <View style={cardStyles.pillRow}>
                  <View style={cardStyles.pill}>
                    <Text style={cardStyles.pillText}>{combinedMeta}</Text>
                  </View>
                </View>
              ) : null}

              {(hasEdu || hasWork) && (
                <View style={[cardStyles.section, cardStyles.educationSection]}>
                  {hasEdu && (
                    <>
                      {card.educationHistory.map((e, i) => {
                        const title = e.level && e.degree
                          ? `${e.level} – ${e.degree}`
                          : (e.level || e.degree || e.qualification || '');
                        const where = e.institution || e.school || (Array.isArray(e.schools) ? e.schools.join(' & ') : '');
                        return (
                          <View key={i} style={cardStyles.row}>
                            <Text style={cardStyles.rowText}>
                              {title ? <Text style={cardStyles.rowTitle}>{title}</Text> : null}
                              {title && where ? ' · ' : ''}
                              {where}
                            </Text>
                          </View>
                        );
                      })}
                    </>
                  )}

                  {hasWork && (
                    <View style={hasEdu ? cardStyles.sectionSubGap : null}>
                      {card.workExperience.map((w, i) => {
                        // Schema (Register2/WorkExperience.js): { status: 'current'|'past'|'other',
                        //   workType, description, location }
                        // Display matches main app Profile.js exactly.
                        const isCurrent = w.status === 'current' || w.current === true || w.isCurrent === true;
                        const isPast = w.status === 'past';
                        const heading = isCurrent
                          ? '🟢 Current Position'
                          : isPast
                            ? '⚪ Past Position'
                            : (w.role || w.title || w.position || 'Position');
                        const desc = w.description || w.role || w.title || w.company || '';
                        const loc = w.location || '';
                        return (
                          <View key={i} style={cardStyles.row}>
                            <Text style={cardStyles.rowText}>
                              <Text style={cardStyles.rowTitle}>{heading}</Text>
                              {desc ? ` · ${desc}` : ''}
                              {loc ? ` · 📍 ${loc}` : ''}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>

        {card.message ? (
          <Text style={cardStyles.message}>{card.message}</Text>
        ) : null}
        </View>
      </View>
    </View>
  );
});

// Status pill metadata for invited public-email recipients (US Vedika).
// `status` values come from the backend's publicParticipants[].status field
// and are enriched onto each message's publicEmailsSent[] in messenger_service.
const PUBLIC_EMAIL_STATUS_META = {
  invited:    { label: 'Pending activation', color: colors.warning, bg: colors.warningA15 },
  interested: { label: 'Interested',         color: colors.info, bg: colors.infoA15 },
  replied:    { label: 'Replied',            color: colors.success, bg: colors.successA15 },
  opted_out:  { label: 'Unsubscribed',       color: colors.textGray400, bg: colors.gray400A15 },
};
const getPublicEmailStatusMeta = (status) =>
  PUBLIC_EMAIL_STATUS_META[status] || PUBLIC_EMAIL_STATUS_META.invited;

// Short, human-readable relative time (e.g. "3d ago", "just now") used in the
// recipient-status bubbles. Falls back to an empty string for invalid input.
const formatRelative = (when) => {
  if (!when) return '';
  const t = typeof when === 'string' ? Date.parse(when) : (when instanceof Date ? when.getTime() : when);
  if (!t || Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const EMPTY_MESSAGES = [];

export default function ChatScreen({ id, name, isGroup, isLegacy, profile, username, isOnline, onBack, onOpenDirectChat }) {
  const { user } = useAuthStore();

  const storeMessages = useMessengerStore((state) => (id ? (state.messages[id] ?? EMPTY_MESSAGES) : EMPTY_MESSAGES));
  const fetchStoreMessages = useMessengerStore((state) => state.fetchMessages);
  const fetchStoreMessagesAfter = useMessengerStore((state) => state.fetchMessagesAfter);
  const sendStoreMessage = useMessengerStore((state) => state.sendMessage);
  const onStoreNewMessage = useMessengerStore((state) => state.onNewMessage);
  const deleteStoreMessage = useMessengerStore((state) => state.deleteMessage);
  const markStoreRead = useMessengerStore((state) => state.markRead);
  const toggleStoreReaction = useMessengerStore((state) => state.toggleReaction);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  // Send errors are kept separate from load errors so they don't blow away
  // the chat view. They appear as an inline banner above the composer.
  const [sendError, setSendError] = useState(null);
  const [retentionStatus, setRetentionStatus] = useState(null);
  // US Vedika public group states
  const [showPublicRecipientModal, setShowPublicRecipientModal] = useState(false);
  const [publicRecipients, setPublicRecipients] = useState([]);
  const [includeInvitation, setIncludeInvitation] = useState(true);
  // Pre-flight check results for @{email} recipients. Populated by
  // /api/messenger/conversations/{id}/check-recipients when the modal opens.
  //   recipientChecks: { [email]: { isMember, memberUsername, hasActiveInvitation,
  //     invitationStatus, invitationSentAt, alreadyInConversation, lastSentAt } }
  //   senderCanInvite: false → disable Send + Invite and show a banner.
  const [recipientChecks, setRecipientChecks] = useState({});
  const [senderCanInvite, setSenderCanInvite] = useState(true);
  const [checkingRecipients, setCheckingRecipients] = useState(false);
  // Per-conversation message retention (TTL). null = off. Backend hard-deletes
  // messages whose expireAt has passed via a TTL index on messenger_messages.
  // Only app-level admins/moderators can change this.
  const [retentionHours, setRetentionHours] = useState(null);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [savingRetention, setSavingRetention] = useState(false);
  const [portalMembersCount, setPortalMembersCount] = useState(null);
  // Derived from the reactive `user` from useAuthStore so role changes
  // (re-login, role promotion) flip the UI without a full reload.
  const isAdminOrModerator = user?.role === 'admin' || user?.role === 'moderator';
  const isL3v3lAgentTopic = !isLegacy && String(name || '').trim().toLowerCase() === 'l3v3l agent';
  const isPortalMembersTopic = !isLegacy && (
    String(name || '').trim().toLowerCase() === 'portal members' ||
    String(name || '').trim().toLowerCase() === 'l3v3l members'
  );
  // Canonical title for the members group (L3V3L Members) shown in the chat header.
  const membersGroupTitle = 'L3V3L Members';
  // Force the canonical label in the header for this topic.
  const headerDisplayName = isPortalMembersTopic ? membersGroupTitle : name;
  // Chat-header avatar for 1:1 conversations — same field precedence as the
  // conversation list (imageVisibility.profilePic -> images[0] -> profileImage).
  const headerAvatarPath = !isGroup && profile
    ? (profile.imageVisibility?.profilePic || profile.images?.[0] || profile.profileImage || profile.avatarUrl)
    : null;
  const headerAvatarUri = headerAvatarPath ? buildImageUrl(String(headerAvatarPath), 'thumb') : null;
  const headerTitle = profile && (profile.firstName || profile.lastName)
    ? `${profile.firstName} ${profile.lastName}`.trim()
    : headerDisplayName;
  const headerInitial = (headerTitle || '?').charAt(0).toUpperCase();
  const isComposerRestrictedTopic = isL3v3lAgentTopic;
  const canComposeInCurrentTopic = !isComposerRestrictedTopic || isAdminOrModerator;

  useEffect(() => {
    let isMounted = true;

    if (!isPortalMembersTopic) {
      setPortalMembersCount(null);
      return () => {
        isMounted = false;
      };
    }

    const fetchPortalMembersCount = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const api = useAuthStore.getState().getApi();
        const res = await api.get('/api/users/active-members/count');
        if (!isMounted) return;
        const count = typeof res.data?.activeCount === 'number' ? res.data.activeCount : null;
        setPortalMembersCount(count);
      } catch (e) {
        if (isMounted) {
          setPortalMembersCount(null);
        }
        logger.warn('⚠️ Failed to load portal members count:', e?.message || e);
      }
    };

    fetchPortalMembersCount();
    const interval = setInterval(fetchPortalMembersCount, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isPortalMembersTopic]);
  const canUsePublicRecipientFlow = isL3v3lAgentTopic && isAdminOrModerator;
  // Clear chat
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearingChat, setClearingChat] = useState(false);
  // Username action modal — opened when a user taps a sender's name in a group
  // chat bubble. Offers View Profile (main app) and Direct Message (1:1 chat).
  const [usernameModalTarget, setUsernameModalTarget] = useState(null);
  // Profile card actions menu
  const [profileCardMenuTarget, setProfileCardMenuTarget] = useState(null);
  // Track favorited usernames to show heart emoji on profile cards
  const [favoritedUsernames, setFavoritedUsernames] = useState(new Set());
  // ScrollView ref for scrolling to bottom
  const scrollViewRef = useRef(null);
  const wasSocketConnectedRef = useRef(true);
  // Quick Messages popup (⚡ button next to composer). Only "Introduction" is
  // wired up; the other categories are visible-but-disabled placeholders.
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  const [sendingProfileCard, setSendingProfileCard] = useState(false);
  const [sending, setSending] = useState(false);
  const [armedDeleteId, setArmedDeleteId] = useState(null);
  const [reactionPickerFor, setReactionPickerFor] = useState(null);
  const [socketConnected, setSocketConnected] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // For non-legacy conversations, render directly from the store to avoid
  // an extra commit per real-time update. For legacy chats, keep the local
  // messages state populated by the REST endpoint.
  const displayMessages = isLegacy ? messages : storeMessages;

  useEffect(() => {
    if (!id || isLegacy) return;
    messengerSocket.joinConversation(id);
    return () => messengerSocket.leaveConversation(id);
  }, [id, isLegacy]);

  // Mark incoming messages read while the conversation is open. Legacy
  // chats already mark read server-side inside the conversation fetch.
  useEffect(() => {
    if (!id || isLegacy || !user?.username) return;
    const unreadIds = storeMessages
      .filter((m) => m?.id && !m.isOptimistic && m.senderUsername !== user.username && m.status !== 'read')
      .map((m) => m.id);
    if (!unreadIds.length) return;
    markStoreRead(unreadIds);
    // Clear the unread badge on the conversation list optimistically.
    useMessengerStore.setState((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, unreadCount: 0 } : c
      ),
    }));
  }, [id, isLegacy, storeMessages, user?.username, markStoreRead]);

  useEffect(() => {
    const unsub = messengerSocket.onStatusChange((connected) => {
      setSocketConnected(connected);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!id || isLegacy || !isPortalMembersTopic) {
      wasSocketConnectedRef.current = socketConnected;
      return;
    }

    const wasConnected = wasSocketConnectedRef.current;
    wasSocketConnectedRef.current = socketConnected;

    // Catch up missed messages once when connection is restored.
    if (wasConnected || !socketConnected) return;

    let cancelled = false;
    (async () => {
      const existing = useMessengerStore.getState().messages[id] ?? EMPTY_MESSAGES;
      const afterId = existing.length ? existing[existing.length - 1]?.id : null;
      if (cancelled) return;

      if (afterId) {
        await fetchStoreMessagesAfter(id, afterId);
      } else {
        await fetchStoreMessages(id);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isLegacy, isPortalMembersTopic, socketConnected, fetchStoreMessages, fetchStoreMessagesAfter]);

  useEffect(() => {
    if (!id || isLegacy || !isPortalMembersTopic || socketConnected) return;

    let cancelled = false;
    const poll = async () => {
      // Skip while the tab is hidden — catch-up happens on socket reconnect.
      if (typeof document !== 'undefined' && document.hidden) return;
      const existing = useMessengerStore.getState().messages[id] ?? EMPTY_MESSAGES;
      const afterId = existing.length ? existing[existing.length - 1]?.id : null;
      if (cancelled) return;

      if (afterId) {
        await fetchStoreMessagesAfter(id, afterId);
      } else {
        await fetchStoreMessages(id);
      }
    };

    // Kick off immediately, then continue until socket recovers.
    poll();
    const timer = setInterval(poll, 12000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id, isLegacy, isPortalMembersTopic, socketConnected, fetchStoreMessages, fetchStoreMessagesAfter]);

  useEffect(() => {
    if (!armedDeleteId) return;
    const t = setTimeout(() => setArmedDeleteId(null), 3000);
    return () => clearTimeout(t);
  }, [armedDeleteId]);

  useEffect(() => {
    if (!retentionStatus) return;
    const t = setTimeout(() => setRetentionStatus(null), 6000);
    return () => clearTimeout(t);
  }, [retentionStatus]);

  // Load user's favorites on mount to populate favoritedUsernames state
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user?.username) return;
      try {
        const api = useAuthStore.getState().getApi();
        const res = await api.get(`/api/users/favorites/${user.username}`);
        const favorites = res.data?.favorites || [];
        const usernameSet = new Set(favorites.map(f => f.username));
        setFavoritedUsernames(usernameSet);
      } catch (e) {
        logger.error('Failed to load favorites:', e);
      }
    };
    loadFavorites();
  }, [user?.username]);

  // Scroll to bottom when messages load or new messages arrive
  const firstMessageIdRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const hasLoadedOnceRef = useRef(false);
  // Animated scroll only when the user just sent a message — background
  // refreshes and socket reconnects scroll instantly, no visible jump.
  const animateNextScrollRef = useRef(false);
  useEffect(() => {
    // Reset scroll tracking when switching conversations
    firstMessageIdRef.current = null;
    lastMessageIdRef.current = null;
    hasLoadedOnceRef.current = false;
  }, [id]);

  useEffect(() => {
    if (!scrollViewRef.current) return;
    if (!Array.isArray(displayMessages) || displayMessages.length === 0) return;

    const firstMsg = displayMessages[0] || {};
    const lastMsg = displayMessages[displayMessages.length - 1] || {};

    const firstId = firstMsg.id || firstMsg._id || firstMsg.tempId || firstMsg.createdAt || null;
    const lastId = lastMsg.id || lastMsg._id || lastMsg.tempId || lastMsg.createdAt || null;

    const prevFirstId = firstMessageIdRef.current;
    const prevLastId = lastMessageIdRef.current;

    // Scroll when:
    // - first render of a conversation (initial load)
    // - the last message changes (append OR optimistic temp->real replacement)
    // Do NOT scroll when only older history is prepended (first changes, last stays the same).
    const shouldScroll =
      !hasLoadedOnceRef.current ||
      (!!lastId && prevLastId !== lastId);

    if (shouldScroll) {
      // Defer so layout has committed before we try to scroll.
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: animateNextScrollRef.current });
        animateNextScrollRef.current = false;
      }, 0);
      hasLoadedOnceRef.current = true;
    }

    firstMessageIdRef.current = firstId;
    lastMessageIdRef.current = lastId;
  }, [displayMessages]);

  // Build a profile_card snapshot from the current user's profile and POST it
  // as a rich message into the active conversation. Legacy (main app) 1:1
  // chats don't support rich types, so we gate the ⚡ button on !isLegacy.
  const sendProfileCard = async () => {
    if (!user?.username || isLegacy) return;
    setShowQuickMessages(false);
    setSendingProfileCard(true);
    animateNextScrollRef.current = true;
    setSendError(null);
    try {
      const store = useAuthStore.getState();
      const api = store.getApi();
      let snapshot = await store.prefetchIntroCard({ force: true });
      if (snapshot && !(snapshot.gender || snapshot.sex)) {
        try {
          const profRes = await api.get(`/api/users/profile/${user.username}?requester=${user.username}`);
          const p = profRes.data?.user || profRes.data || {};
          const genderValue = p.sex || p.gender || null;
          if (genderValue) {
            snapshot = {
              ...snapshot,
              gender: genderValue,
              sex: genderValue,
            };
          }
        } catch (_) {
          // Best-effort enrichment — continue with existing snapshot
        }
      }
      if (!snapshot) {
        // Fetch the freshest profile so the snapshot reflects the latest edits.
        const profRes = await api.get(`/api/users/profile/${user.username}?requester=${user.username}`);
        const p = profRes.data?.user || profRes.data || {};
        const fullName = `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.username || user.username;
        // Use gender from profile data (sex field in DB), fallback to current user's gender
        const gender = p.sex || p.gender || user.gender || user.sex || null;
        // The main app uses birthMonth / birthYear (no day). Synthesize a label
        // like "11/1995" for the card pill, plus an ISO-ish dob fallback for
        // anything that wants a real date.
        const rawDob = p.dob || p.dateOfBirth || null;
        const age = calcAge(rawDob, p.birthYear, p.birthMonth);
        let dobLabel = null;
        if (rawDob) {
          const parsed = new Date(rawDob);
          if (!Number.isNaN(parsed.getTime())) {
            dobLabel = parsed.toLocaleDateString('en-US', { month: '2-digit', year: 'numeric' });
          }
        } else if (p.birthMonth && p.birthYear) {
          dobLabel = `${String(p.birthMonth).padStart(2, '0')}/${p.birthYear}`;
        }
        const avatarPath = p.imageVisibility?.profilePic || p.images?.[0] || p.profileImage || null;
        // Height is stored in one of three shapes across the codebase:
        //   1. p.height           — already-formatted string ("5'8\"")
        //   2. p.heightFeet + p.heightInches (inches 0-11) — Register form shape
        //   3. p.heightInches alone — total inches int (search results)
        let heightLabel = null;
        if (p.height && String(p.height).trim()) {
          heightLabel = String(p.height).trim();
        } else if (p.heightFeet) {
          const inches = Number(p.heightInches) || 0;
          heightLabel = `${p.heightFeet}'${inches}"`;
        } else if (p.heightInches && Number(p.heightInches) > 11) {
          const total = Number(p.heightInches);
          heightLabel = `${Math.floor(total / 12)}'${total % 12}"`;
        }
        // Profile schema (see fastapi_backend/models/user_models.py + Register2.js):
        //   educationHistory: [{ level, degree, institution }]
        //   workExperience:   [{ status: 'current'|'past', workType, description, location }]
        //   partnerPreference: free-text "what I'm looking for" blurb
        //   customPartnerPreference: admin/user override (preferred when present)
        snapshot = {
          username: p.username || user.username,
          fullName,
          // Persist the raw path / token-less URL. buildImageUrl() injects
          // the CURRENT JWT at render time. Baking the token into the
          // snapshot would expire it ~30 min later → 401 → empty avatar.
          avatarUrl: avatarPath,
          age,
          dob: rawDob,
          dobLabel,
          height: heightLabel,
          gender,
          sex: gender,
          // Just the city — Education / Work are shown in their own sections.
          location: p.location || p.currentLocation || null,
          // Persist arrays exactly as stored — the renderer knows the schema.
          educationHistory: Array.isArray(p.educationHistory) ? p.educationHistory : [],
          workExperience: Array.isArray(p.workExperience) ? p.workExperience : [],
          // Static blurb at the bottom of the Introduction card. The wording is
          // gendered against the profile holder so it reads correctly to the
          // recipient: a male profile is "looking for a bride for our son",
          // a female profile is "looking for a groom for our daughter".
          // Falls back to a neutral phrasing if gender is missing/unknown.
          message: (() => {
            const g = String(gender || '').trim().toLowerCase();
            if (g === 'male' || g === 'm' || g === 'man') {
              return 'Looking for a suitable bride for our son — please review the profile for details and Contact me. Thanks';
            }
            if (g === 'female' || g === 'f' || g === 'woman') {
              return 'Looking for a suitable groom for our daughter — please review the profile for details and Contact me. Thanks';
            }
            return 'Looking for a suitable match — please review the profile for details and Contact me. Thanks';
          })(),
        };
      }
      const res = await api.post(`/api/messenger/conversations/${id}/messages`, {
        conversationId: id,
        contentType: 'profile_card',
        content: snapshot.message || '',
        cardSnapshot: snapshot,
      });

      const created = res?.data?.message;
      if (created) {
        // Legacy chats render from local `messages` state; non-legacy chats
        // render from the messenger store. Update whichever is in use so the
        // card shows immediately without waiting for a refresh / socket echo.
        if (isLegacy) {
          setMessages((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            next.push(created);
            return next;
          });
        } else {
          onStoreNewMessage(id, created);
        }
      } else {
        await loadMessages();
      }
    } catch (e) {
      logger.error('❌ Failed to send profile card:', e);
      const detail = e?.response?.data?.detail;
      setSendError(typeof detail === 'string' && detail ? detail : 'Failed to send profile card');
    } finally {
      setSendingProfileCard(false);
    }
  };

  const handleUsernameClick = (uname) => {
    if (!uname || uname === user.username) return; // ignore own name / empty
    setUsernameModalTarget(uname);
  };

  // Stable handlers for ProfileCard — memo() only helps if prop identity is
  // stable across renders (inline arrows would defeat it).
  const handleCardMenuOpen = useCallback((card) => {
    setProfileCardMenuTarget(card);
  }, []);
  const handleCardUsernameClick = useCallback((uname) => {
    // Tapping the profile name on a card jumps straight to the main app's
    // /profile/:username page — skips the View-Profile/Direct-Message chooser
    // modal that group-chat sender names use (intent is unambiguous here).
    if (!uname) return;
    const url = `${getMainAppUrl()}/profile/${encodeURIComponent(uname)}`;
    openExternalUrl(url);
  }, []);

  // Returns the main-app URL to open when a L3V3L Agent notification bubble is tapped.
  // Returns null for non-notification messages.
  const getNotificationLink = (content) => {
    if (!content) return null;
    const base = getMainAppUrl();
    if (content.includes('*New Matches*'))       return `${base}/search`;
    if (content.includes('*Pending Messages*'))  return `${base}/messages`;
    if (content.includes('*Tip*'))               return `${base}/edit-profile`;
    if (content.includes('*Poll Expirations*'))  return `${base}/polls`;
    return null;
  };

  const openNotificationLink = (url) => {
    if (!url) return;
    openExternalUrl(url);
  };

  const handleViewProfile = () => {
    const uname = usernameModalTarget;
    setUsernameModalTarget(null);
    if (!uname) return;
    const url = `${getMainAppUrl()}/profile/${encodeURIComponent(uname)}`;
    openExternalUrl(url);
  };

  const handleDirectMessage = () => {
    const uname = usernameModalTarget;
    setUsernameModalTarget(null);
    if (!uname) return;
    if (typeof onOpenDirectChat === 'function') {
      onOpenDirectChat(uname);
    }
  };

  // Profile card action handlers
  const handleAddToFavorites = async (card) => {
    setProfileCardMenuTarget(null);
    const username = card.username;
    if (!username) return;
    try {
      const api = useAuthStore.getState().getApi();
      await api.post(`/api/users/favorites/${username}`);
      setFavoritedUsernames(prev => new Set([...prev, username]));
    } catch (e) {
      logger.error('Failed to add to favorites:', e);
    }
  };

  const handleRemoveFromFavorites = async (card) => {
    setProfileCardMenuTarget(null);
    const username = card.username;
    if (!username) return;
    try {
      const api = useAuthStore.getState().getApi();
      await api.delete(`/api/users/favorites/${username}`);
      setFavoritedUsernames(prev => {
        const newSet = new Set(prev);
        newSet.delete(username);
        return newSet;
      });
    } catch (e) {
      logger.error('Failed to remove from favorites:', e);
    }
  };

  const handleDirectMessageFromCard = (card) => {
    setProfileCardMenuTarget(null);
    const username = card.username;
    if (!username) return;
    if (typeof onOpenDirectChat === 'function') {
      onOpenDirectChat(username);
    }
  };

  const handleViewProfileFromCard = (card) => {
    setProfileCardMenuTarget(null);
    const username = card.username;
    if (!username) return;
    const url = `${getMainAppUrl()}/profile/${encodeURIComponent(username)}`;
    openExternalUrl(url);
  };

  const handleClearChat = async () => {
    setClearingChat(true);
    try {
      const api = useAuthStore.getState().getApi();
      if (isLegacy) {
        await api.post(`/api/users/messages/conversation/${name}/clear`);
      } else {
        await api.post(`/api/messenger/conversations/${id}/clear`);
      }
      setShowClearConfirm(false);
      if (isLegacy) {
        setMessages([]);
        await loadMessages();
      } else {
        useMessengerStore.setState((state) => ({
          messages: { ...(state.messages || {}), [id]: [] },
        }));
        await fetchStoreMessages(id);
      }
      logger.debug('🧹 Chat cleared from view');
    } catch (e) {
      logger.error('❌ Failed to clear chat:', e);
      setError(e?.response?.data?.detail || 'Failed to clear chat');
    } finally {
      setClearingChat(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadMessages();
      // Fetch the conversation doc once to read messageRetentionHours so the
      // ⏱️ clock button shows the current value. Cheap GET; non-fatal on error.
      if (!isLegacy) {
        (async () => {
          try {
            const api = useAuthStore.getState().getApi();
            const res = await api.get(`/api/messenger/conversations/${id}?lite=true&include_unread_count=false`);
            const r = res?.data?.conversation?.messageRetentionHours;
            setRetentionHours(typeof r === 'number' && r > 0 ? r : null);
          } catch (e) {
            logger.warn('⚠️ Failed to load conversation retention:', e?.message);
          }
        })();
      }
    }
  }, [id, isLegacy]);

  // Persist a new retention setting (admin/moderator only). `hours` may be
  // null to turn TTL off, or a positive integer to enable auto-delete.
  const saveRetention = async (hours) => {
    if (!isAdminOrModerator || isLegacy) return;
    setSavingRetention(true);
    setSendError(null);
    setRetentionStatus(null);
    try {
      const api = useAuthStore.getState().getApi();
      const res = await api.put(`/api/messenger/conversations/${id}/retention`, {
        retentionHours: hours,
      });
      const retentionHoursValue = Number(res?.data?.retentionHours);
      const nextRetention = Number.isFinite(retentionHoursValue) && retentionHoursValue > 0
        ? retentionHoursValue
        : null;
      setRetentionHours(nextRetention);
      await fetchStoreMessages(id);

      const purged = Number(res?.data?.purgedExistingMessages ?? 0);
      const updated = Number(res?.data?.updatedExistingMessages ?? 0);
      const retentionLabel =
        typeof nextRetention === 'number' && nextRetention > 0
          ? (nextRetention >= 24 && nextRetention % 24 === 0 ? `${nextRetention / 24}d` : `${nextRetention}h`)
          : 'off';

      if (nextRetention === null) {
        setRetentionStatus(
          `Retention turned off. Removed expiry from ${updated} message${updated === 1 ? '' : 's'}.`
        );
      } else {
        const scheduledText =
          updated > 0
            ? ` ${updated} message${updated === 1 ? '' : 's'} scheduled for expiry.`
            : '';
        setRetentionStatus(
          `Retention set to ${retentionLabel}. Purged ${purged} old message${purged === 1 ? '' : 's'}.${scheduledText}`
        );
      }

      setShowRetentionModal(false);
    } catch (e) {
      logger.error('❌ Failed to save retention:', e);
      const detail = e?.response?.data?.detail;
      setRetentionStatus(null);
      setSendError(typeof detail === 'string' ? detail : 'Failed to update retention');
      setShowRetentionModal(false);
    } finally {
      setSavingRetention(false);
    }
  };

  const loadMessages = async () => {
    // Full-pane spinner only when there's nothing to render yet — refreshes
    // over existing messages keep the list mounted (subtle refresh).
    const hasContent = isLegacy
      ? messages.length > 0
      : (useMessengerStore.getState().messages[id] ?? EMPTY_MESSAGES).length > 0;
    if (!hasContent) setLoading(true);
    setError(null);
    try {
      logger.debug('📬 Loading messages for conversation:', id, 'isLegacy:', isLegacy, 'username:', username);
      const api = useAuthStore.getState().getApi();
      let res;
      if (isLegacy) {
        // Legacy 1:1 main app messages
        const targetUsername = (username || name || '').trim();
        if (!targetUsername) {
          throw new Error('Missing conversation username');
        }
        const encodedTarget = encodeURIComponent(targetUsername);
        const encodedSelf = encodeURIComponent(user?.username || '');
        res = await api.get(`/api/users/messages/conversation/${encodedTarget}?username=${encodedSelf}`);
      } else {
        await fetchStoreMessages(id);
        setMessages(useMessengerStore.getState().messages[id] ?? EMPTY_MESSAGES);
        return;
      }
      logger.debug('✅ API Response:', res.data);

      // Handle different response structures
      const msgs = Array.isArray(res.data) ? res.data : res.data.messages || [];
      logger.debug('✅ Messages loaded:', msgs.length);
      setMessages(msgs);
    } catch (e) {
      logger.error('❌ Failed to load messages:', e);
      setError(e?.response?.data?.detail || e?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  // Parse @{email} mentions from message content
  // Supports both @{email} and @email formats
  const parsePublicRecipients = (text) => {
    const recipients = [];

    // Try @{email} format first
    const curlyBraceRegex = /@\{([^}]+)\}/g;
    let match;
    while ((match = curlyBraceRegex.exec(text)) !== null) {
      const content = match[1];
      // Parse "Name <email>" or just "email"
      const emailMatch = content.match(/(?:([^<]+)<)?([^>]+)>?/);
      if (emailMatch) {
        const displayName = emailMatch[1]?.trim();
        const email = emailMatch[2]?.trim();
        if (email && email.includes('@')) {
          recipients.push({
            email,
            displayName: displayName || email,
          });
        }
      }
    }

    // Also support simple @email format (without curly braces)
    const simpleEmailRegex = /@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    while ((match = simpleEmailRegex.exec(text)) !== null) {
      const email = match[1];
      recipients.push({
        email,
        displayName: email.split('@')[0],
      });
    }

    return recipients;
  };

  // Run the pre-flight check that powers the status bubbles in the modal.
  // Tolerant to backend errors: we still open the modal so the user can decide,
  // but bubbles won't render. The actual send is still server-enforced.
  const runRecipientCheck = async (recipients) => {
    setRecipientChecks({});
    setCheckingRecipients(true);
    try {
      const api = useAuthStore.getState().getApi();
      const res = await api.post(
        `/api/messenger/conversations/${id}/check-recipients`,
        { emails: recipients.map((r) => r.email) },
      );
      const byEmail = {};
      for (const r of (res.data?.recipients || [])) {
        byEmail[(r.email || '').toLowerCase()] = r;
      }
      setRecipientChecks(byEmail);
      // Trust the server's role evaluation rather than re-deriving from local
      // auth state — keeps the UI consistent with the backend gate.
      setSenderCanInvite(res.data?.senderCanInvite !== false);
    } catch (e) {
      logger.warn('⚠️ check-recipients failed, modal will render without bubbles:', e?.message);
      // Fail-open on bubbles but fail-closed on role if the user has no role.
      const localRole = useAuthStore.getState().user?.role;
      setSenderCanInvite(localRole === 'admin' || localRole === 'moderator');
    } finally {
      setCheckingRecipients(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    if (!canComposeInCurrentTopic) {
      setSendError('Only administrators or moderators can send messages in the L3V3L Agent topic.');
      return;
    }

    // Check for public recipients in L3V3L Agent @{email} flow.
    // Only admins/moderators can invite via @{email}; for everyone else the
    // token is treated as plain text — no modal, no parsing, no actionable
    // recipient. The backend also enforces the role gate (defense in depth);
    // this client-side check is a UX cleanup so non-privileged users don't see
    // a modal that would just be blocked anyway.
    if (canUsePublicRecipientFlow) {
      const recipients = parsePublicRecipients(newMessage);
      if (recipients.length > 0) {
        // Show modal for public recipients + kick off pre-flight in parallel
        setPublicRecipients(recipients);
        setShowPublicRecipientModal(true);
        runRecipientCheck(recipients); // fire-and-forget; updates state when done
        return;
      }
    }

    setSending(true);
    animateNextScrollRef.current = true;
    try {
      logger.debug('📤 Sending message:', isLegacy ? 'legacy' : 'messenger', id);
      const api = useAuthStore.getState().getApi();
      let res;
      if (isLegacy) {
        const targetUsername = (username || name || '').trim();
        if (!targetUsername) {
          throw new Error('Missing conversation username');
        }
        res = await api.post('/api/users/messages/send', {
          toUsername: targetUsername,
          content: newMessage.trim(),
        });
      } else {
        const msg = await sendStoreMessage(id, newMessage.trim(), 'text');
        if (!msg) {
          throw new Error('Failed to send message');
        }
        res = { data: { success: true, message: msg } };
      }
      logger.debug('✅ Message sent successfully:', res.data);
      const sentText = newMessage.trim();
      setNewMessage('');
      if (isLegacy) {
        // Append the sent message locally instead of a full reload — avoids
        // the spinner flash and scroll reset after every send.
        const sent = res.data?.data;
        const target = (username || name || '').trim();
        setMessages((prev) => [
          ...(Array.isArray(prev) ? prev : []),
          {
            id: sent?.id || `local-${Date.now()}`,
            fromUsername: user.username,
            toUsername: target,
            content: sentText,
            isRead: false,
            createdAt: sent?.timestamp || new Date().toISOString(),
          },
        ]);
      }
    } catch (e) {
      logger.error('❌ Failed to send message:', e);
      logger.error('❌ Error response:', e.response?.data);
      // Surface the backend's detail string so users (and you) can see WHY
      // a 403/400 was raised — e.g. paused account, exclusion, profanity.
      const detail = e?.response?.data?.detail;
      setSendError(typeof detail === 'string' && detail ? detail : 'Failed to send message');
      // Keep `newMessage` populated so the user can tap Send again to retry.
    } finally {
      setSending(false);
    }
  };

  const sendWithPublicRecipients = async (deliveryMode) => {
    setShowPublicRecipientModal(false);
    setRecipientChecks({});
    setSending(true);
    try {
      logger.debug('📤 Sending message with public recipients:', publicRecipients);
      const api = useAuthStore.getState().getApi();
      const res = await api.post(`/api/messenger/conversations/${id}/messages`, {
        conversationId: id,
        contentType: 'text',
        content: newMessage.trim(),
        publicRecipients,
        deliveryMode,
        includeInvitation,
      });
      logger.debug('✅ Message sent successfully:', res.data);
      setNewMessage('');
      setPublicRecipients([]);
      if (res?.data?.message) {
        onStoreNewMessage(id, res.data.message);
      }
    } catch (e) {
      logger.error('❌ Failed to send message:', e);
      logger.error('❌ Error response:', e.response?.data);
      const detail = e?.response?.data?.detail;
      setSendError(typeof detail === 'string' && detail ? detail : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <View style={styles.container} className="chat-screen">
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Text style={styles.backButtonText}>←</Text>
        </TouchableOpacity>
        {!isGroup && (
          headerAvatarUri ? (
            <Image source={{ uri: headerAvatarUri }} style={styles.headerAvatar} />
          ) : (
            <View style={[styles.headerAvatar, styles.headerAvatarFallback]}>
              <Text style={styles.headerAvatarFallbackText}>{headerInitial}</Text>
            </View>
          )
        )}
        <View style={styles.headerInfo}>
          <View style={styles.headerTitleRow}>
            <Text style={styles.headerTitle} numberOfLines={1}>
              {headerTitle}
            </Text>
            {isPortalMembersTopic && typeof portalMembersCount === 'number' && (
              <View style={styles.headerCountBadge}>
                <Text style={styles.headerCountText}>
                  {portalMembersCount > 999 ? '999+' : portalMembersCount}
                </Text>
              </View>
            )}
          </View>
          {isGroup ? (
            <Text style={styles.headerSubtitle}>Group Chat</Text>
          ) : username && typeof isOnline === 'function' ? (
            <View style={styles.headerStatusRow}>
              <View
                style={[
                  styles.headerStatusInlineDot,
                  isOnline(username) && styles.headerStatusInlineDotOnline,
                ]}
              />
              <Text
                style={[
                  styles.headerSubtitle,
                  isOnline(username) && styles.headerSubtitleOnline,
                ]}
              >
                {isOnline(username) ? 'Online' : 'Offline'}
              </Text>
            </View>
          ) : null}
        </View>
        {/* ⏱️ Retention button — shows current TTL (e.g. "24h"); opens picker.
            Admin/moderator only. Hidden on legacy main-app chats since retention
            is a messenger-only feature. */}
        {!isLegacy && isAdminOrModerator && (
          <TouchableOpacity
            style={styles.iconButton}
            onPress={() => setShowRetentionModal(true)}
            disabled={savingRetention}
          >
            <Text style={styles.headerActionEmoji}>⏱️</Text>
            {retentionHours ? (
              <Text style={styles.iconButtonBadge}>
                {retentionHours >= 24 && retentionHours % 24 === 0
                  ? `${retentionHours / 24}d`
                  : `${retentionHours}h`}
              </Text>
            ) : null}
          </TouchableOpacity>
        )}
        {/* Clear Chat — admin/moderator only (destructive action) */}
        {isAdminOrModerator && (
          <TouchableOpacity
            style={styles.iconButton}
            onPress={() => setShowClearConfirm(true)}
          >
            <Text style={styles.headerActionEmoji}>🗑️</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Messages and Input container */}
      <View style={styles.body} className="chat-screen__body">
        <View style={styles.messagesWrapper} className="chat-screen__messages-wrapper">
          {/* Messages */}
          {loading && displayMessages.length === 0 ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.accent} />
            <Text style={styles.loadingText}>Loading messages...</Text>
          </View>
        ) : error && displayMessages.length === 0 ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity style={styles.retryBtn} onPress={loadMessages}>
              <Text style={styles.retryBtnText}>Retry</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <ScrollView
            ref={scrollViewRef}
            style={styles.messagesContainer}
            className="chat-screen__messages-scroll"
            contentContainerStyle={styles.messagesContent}
            keyboardShouldPersistTaps="handled"
          >
          {!socketConnected && (
            <View style={styles.reconnectBanner}>
              <Text style={styles.reconnectBannerText}>
                Reconnecting… new messages will appear once you're back online.
              </Text>
            </View>
          )}
          {displayMessages.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No messages yet</Text>
              <Text style={styles.emptySubText}>Start the conversation!</Text>
            </View>
          ) : (
            displayMessages.map((msg) => {
              const isOwn = msg.senderUsername === user.username || msg.fromUsername === user.username;
              const isPublicEmail = msg.senderType === 'public_email';
              const canDelete = !isLegacy && isOwn && !msg.isDeleted && Boolean(msg.id) && !msg.isOptimistic;
              const isActivationIntro =
                msg.senderUsername === 'L3V3LMatchAgent' &&
                msg.contentType === 'profile_card' &&
                msg.cardSnapshot &&
                (
                  msg.cardSnapshot.systemTag === 'newly_activated' ||
                  msg.cardSnapshot.systemTag === 'reactivated' ||
                  msg.cardSnapshot.systemTag === 'updated'
                );
              const senderName = isGroup && !isOwn ? (msg.senderUsername || msg.fromUsername || 'Unknown') : null;
              const publicEmailsSent = Array.isArray(msg.publicEmailsSent) ? msg.publicEmailsSent : [];
              const notifUrl = msg.senderUsername === 'l3v3lagent' ? getNotificationLink(msg.content) : null;
              const canReact = !isLegacy && !msg.isDeleted && Boolean(msg.id) && !msg.isOptimistic;
              const reactionEntries = canReact
                ? Object.entries(msg.reactions || {}).filter(([, users]) => (users || []).length > 0)
                : [];
              return (
                <View key={msg.id} style={[styles.messageRow, isOwn && styles.messageRowOwn]}>
                  {canReact && reactionPickerFor === msg.id && (
                    <View style={[styles.reactionPicker, isOwn && styles.reactionPickerOwn]}>
                      {REACTION_EMOJIS.map((emoji) => (
                        <TouchableOpacity
                          key={emoji}
                          style={styles.reactionPickerBtn}
                          onPress={() => {
                            setReactionPickerFor(null);
                            toggleStoreReaction(msg.id, id, emoji);
                          }}
                        >
                          <Text style={styles.reactionPickerEmoji}>{emoji}</Text>
                        </TouchableOpacity>
                      ))}
                      <TouchableOpacity
                        style={styles.reactionPickerBtn}
                        onPress={() => setReactionPickerFor(null)}
                      >
                        <Text style={styles.reactionPickerClose}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                  <TouchableOpacity
                    onPress={notifUrl ? () => openNotificationLink(notifUrl) : undefined}
                    onLongPress={canReact ? () => setReactionPickerFor(reactionPickerFor === msg.id ? null : msg.id) : undefined}
                    activeOpacity={notifUrl ? 0.75 : 1}
                    style={[
                    styles.messageBubble,
                    isOwn && styles.messageBubbleOwn,
                    isPublicEmail && styles.messageBubblePublic,
                    msg.contentType === 'profile_card' && styles.messageBubbleCard,
                    notifUrl && styles.messageBubbleNotif,
                  ]}>
                    {isPublicEmail ? (
                      <Text style={styles.publicEmailBadge}>📧 {msg.publicEmail || msg.senderUsername}</Text>
                    ) : isActivationIntro ? (
                      <View style={styles.systemIntroHeader}>
                        <Text style={styles.systemSender}>{msg.senderUsername}</Text>
                        <View style={styles.systemBadge}>
                          <Text style={styles.systemBadgeText}>
                            {msg.cardSnapshot.systemLabel || 'Newly activated'}
                          </Text>
                        </View>
                      </View>
                    ) : isGroup && senderName && !isOwn && (
                      <TouchableOpacity onPress={() => handleUsernameClick(senderName)}>
                        <Text style={styles.senderName}>{senderName}</Text>
                      </TouchableOpacity>
                    )}
                    {msg.isDeleted ? (
                      <Text style={[styles.messageText, isOwn && styles.messageTextOwn]}>Message deleted</Text>
                    ) : msg.contentType === 'profile_card' && msg.cardSnapshot ? (
                      <ProfileCard
                        card={msg.cardSnapshot}
                        isOwn={isOwn}
                        isFavorited={favoritedUsernames.has(msg.cardSnapshot.username)}
                        currentUserGender={user?.gender}
                        onMenuOpen={handleCardMenuOpen}
                        onUsernameClick={handleCardUsernameClick}
                      />
                    ) : msg.content ? (
                      <Text style={[styles.messageText, isOwn && styles.messageTextOwn]}>
                        {msg.content}
                      </Text>
                    ) : null}

                    {/* US Vedika invitation recipients — show current activation
                        status for each @{email} that was invited via this message. */}
                    {publicEmailsSent.length > 0 && (
                      <View style={styles.recipientsList}>
                        <Text style={styles.recipientsListTitle}>
                          Invited recipient{publicEmailsSent.length > 1 ? 's' : ''}
                        </Text>
                        {publicEmailsSent.map((r, idx) => {
                          const meta = getPublicEmailStatusMeta(r.status);
                          return (
                            <View
                              key={`${r.email}-${idx}`}
                              style={styles.recipientRow}
                            >
                              <Text style={styles.recipientEmail} numberOfLines={1}>
                                📧 {r.displayName && r.displayName !== r.email
                                  ? `${r.displayName} <${r.email}>`
                                  : r.email}
                              </Text>
                              <View
                                style={[
                                  styles.recipientStatusPill,
                                  { backgroundColor: meta.bg, borderColor: meta.color },
                                ]}
                              >
                                <Text style={[styles.recipientStatusText, { color: meta.color }]}>
                                  {meta.label}
                                </Text>
                              </View>
                            </View>
                          );
                        })}
                      </View>
                    )}

                    {notifUrl && (
                      <Text style={styles.notifBubbleTapHint}>Tap to open in main app ↗</Text>
                    )}
                    <View style={styles.messageMetaRow}>
                      <Text style={styles.messageTime}>
                        {msg.status === 'sending' ? 'Sending…'
                          : msg.status === 'failed' ? 'Failed — tap Send to retry'
                          : (() => {
                              const d = new Date(msg.createdAt);
                              if (Number.isNaN(d.getTime())) return '';
                              const date = d.toLocaleDateString([], { month: '2-digit', day: '2-digit' });
                              const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                              return `${date} ${time}`;
                            })()}
                      </Text>
                      {/* WhatsApp-style receipt on own messages: ✓ sent, ✓✓ delivered, blue ✓✓ read.
                          Legacy convs expose isRead (bool) instead of readAt/status. */}
                      {isOwn && msg.status !== 'sending' && msg.status !== 'failed' && (
                        <Text style={[styles.messageTime, (msg.readAt || msg.isRead) && styles.messageReceiptRead]}>
                          {msg.readAt || msg.isRead ? '✓✓' : msg.deliveredAt ? '✓✓' : '✓'}
                        </Text>
                      )}
                      {canDelete && (
                        <TouchableOpacity
                          onPress={async () => {
                            if (armedDeleteId !== msg.id) {
                              setArmedDeleteId(msg.id);
                              return;
                            }
                            setArmedDeleteId(null);
                            const ok = await deleteStoreMessage(msg.id, id);
                            if (!ok) setSendError('Failed to delete message');
                          }}
                          style={styles.messageActionBtn}
                        >
                          <Text style={[styles.messageTime, styles.messageActionIcon]}>
                            {armedDeleteId === msg.id ? '✓' : '🗑️'}
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                    {reactionEntries.length > 0 && (
                      <View style={styles.reactionRow}>
                        {reactionEntries.map(([emoji, users]) => {
                          const mine = (users || []).includes(user.username);
                          return (
                            <TouchableOpacity
                              key={emoji}
                              style={[styles.reactionChip, mine && styles.reactionChipMine]}
                              onPress={() => toggleStoreReaction(msg.id, id, emoji)}
                            >
                              <Text style={styles.reactionChipText}>
                                {emoji} {users.length}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              );
            })
          )}
        </ScrollView>
      )}
        </View>

        {/* Input */}
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'position'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
          className="chat-screen__composer"
        >
          {/* Inline send-error banner */}
          {sendError && (
            <View style={styles.sendErrorBanner}>
              <Text style={styles.sendErrorText} numberOfLines={3}>{sendError}</Text>
              <TouchableOpacity onPress={() => setSendError(null)} style={styles.sendErrorDismiss}>
                <Text style={styles.sendErrorDismissText}>✕</Text>
              </TouchableOpacity>
            </View>
          )}
          {retentionStatus && (
            <View style={styles.retentionInfoBanner}>
              <Text style={styles.retentionInfoText} numberOfLines={3}>{retentionStatus}</Text>
              <TouchableOpacity onPress={() => setRetentionStatus(null)} style={styles.retentionInfoDismiss}>
                <Text style={styles.retentionInfoDismissText}>✕</Text>
              </TouchableOpacity>
            </View>
          )}
          {isL3v3lAgentTopic && !isAdminOrModerator && (
            <View style={styles.composerLockBanner}>
              <Text style={styles.composerLockBannerText}>
                Read-only topic: only admin/moderator users can send messages in L3V3L Agent.
              </Text>
            </View>
          )}
          <View
            style={[styles.inputContainer, isMobile && styles.inputContainerMobile]}
            className={`chat-screen__input${isMobile ? ' chat-screen__input--mobile' : ''}`}
          >
            {!isLegacy && isPortalMembersTopic && (
              <TouchableOpacity
                style={[styles.quickBtn, isMobile && styles.quickBtnMobile]}
                onPress={() => setShowQuickMessages(true)}
                disabled={sendingProfileCard}
              >
                {sendingProfileCard
                  ? <ActivityIndicator size="small" color={colors.white} />
                  : <Text style={styles.quickBtnText}>⚡</Text>}
              </TouchableOpacity>
            )}
            <TextInput
              style={[styles.input, isMobile && styles.inputMobile, !canComposeInCurrentTopic && styles.inputDisabled]}
              value={newMessage}
              onChangeText={(t) => { setNewMessage(t); if (sendError) setSendError(null); }}
              placeholder={canComposeInCurrentTopic ? 'Type a message...' : 'Read-only for your role in L3V3L Agent'}
              placeholderTextColor={colors.textDim}
              multiline
              editable={canComposeInCurrentTopic && !sending}
              onKeyPress={({ nativeEvent }) => {
                if (nativeEvent.key === 'Enter' && (nativeEvent.ctrlKey || nativeEvent.metaKey)) {
                  sendMessage();
                }
              }}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                isMobile && styles.sendButtonMobile,
                (!newMessage.trim() || !canComposeInCurrentTopic) && styles.sendButtonDisabled,
              ]}
              onPress={sendMessage}
              disabled={sending || !newMessage.trim() || !canComposeInCurrentTopic}
            >
              {sending ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text style={styles.sendButtonText}>{isMobile ? '➤' : 'Send'}</Text>
              )}
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </View>

      {/* Quick Messages picker — opens from the ⚡ button. Only "Introduction"
          is wired up to a real action (sends a profile_card). The others are
          visible placeholders so users see the planned set. */}
      {showQuickMessages && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>⚡ Quick Messages</Text>
            <View style={styles.quickGrid}>
              {QUICK_MESSAGE_CATEGORIES.map((c) => (
                <TouchableOpacity
                  key={c.id}
                  style={[styles.quickChip, !c.enabled && styles.quickChipDisabled]}
                  onPress={() => {
                    if (!c.enabled) return;
                    if (c.id === 'introduction') sendProfileCard();
                  }}
                  disabled={!c.enabled}
                  activeOpacity={c.enabled ? 0.7 : 1}
                >
                  <Text style={styles.quickChipText}>
                    {c.icon} {c.label}{!c.enabled ? '  ·  soon' : ''}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TouchableOpacity
              style={styles.modalButtonCancel}
              onPress={() => setShowQuickMessages(false)}
            >
              <Text style={styles.modalButtonTextCancel}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Retention picker — Off / 24h / 48h / 7d. Hits PUT /retention which
          is gated on admin/moderator server-side; the UI is also gated above. */}
      {showRetentionModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>⏱️ Message Retention</Text>
            <Text style={styles.modalText}>
              Messages older than the chosen window are removed now, and new
              messages auto-delete after that window.
            </Text>
            <View style={styles.quickGrid}>
              {[
                { label: 'Off',     value: null },
                { label: '24 hours', value: 24 },
                { label: '48 hours', value: 48 },
                { label: '7 days',   value: 24 * 7 },
              ].map((opt) => {
                const selected = (retentionHours || null) === opt.value;
                return (
                  <TouchableOpacity
                    key={opt.label}
                    style={[styles.quickChip, selected && styles.quickChipSelected]}
                    onPress={() => saveRetention(opt.value)}
                    disabled={savingRetention}
                  >
                    <Text style={styles.quickChipText}>
                      {selected ? '✓ ' : ''}{opt.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>
            <TouchableOpacity
              style={styles.modalButtonCancel}
              onPress={() => setShowRetentionModal(false)}
              disabled={savingRetention}
            >
              <Text style={styles.modalButtonTextCancel}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Clear Chat Confirmation Modal */}
      {showClearConfirm && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>🧹 Clear Chat?</Text>
            <Text style={styles.modalText}>
              This will hide all current messages from your view only.
              {isGroup ? ' Other participants will still see them.' : ' The other person will still see the messages on their side.'}
            </Text>
            <Text style={styles.modalText}>
              New messages will continue to appear normally.
            </Text>

            <TouchableOpacity
              style={styles.modalButtonPrimary}
              onPress={handleClearChat}
              disabled={clearingChat}
            >
              <Text style={styles.modalButtonTextPrimary}>
                {clearingChat ? 'Clearing...' : 'Yes, Clear Chat'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.modalButtonCancel}
              onPress={() => setShowClearConfirm(false)}
              disabled={clearingChat}
            >
              <Text style={styles.modalButtonTextCancel}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Profile Card Actions Modal — appears when user taps ⋮ on a profile card */}
      {profileCardMenuTarget && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>👤 {profileCardMenuTarget.fullName || profileCardMenuTarget.username}</Text>
            <Text style={styles.modalText}>What would you like to do?</Text>

            {(() => {
              const normalizeGender = (g) => g ? String(g).trim().toLowerCase() : '';
              const currentG = normalizeGender(user?.gender);
              const cardG = normalizeGender(profileCardMenuTarget.gender || profileCardMenuTarget.sex);
              const isMale = ['male', 'm', 'man'].includes(currentG);
              const cardIsMale = ['male', 'm', 'man'].includes(cardG);
              const isFemale = ['female', 'f', 'woman'].includes(currentG);
              const cardIsFemale = ['female', 'f', 'woman'].includes(cardG);
              // Show actions if either gender is missing OR if genders are opposite
              return !currentG || !cardG || (isMale && cardIsFemale) || (isFemale && cardIsMale);
            })() && (
              <>
                <TouchableOpacity
                  style={styles.modalButtonPrimary}
                  onPress={() => favoritedUsernames.has(profileCardMenuTarget.username)
                    ? handleRemoveFromFavorites(profileCardMenuTarget)
                    : handleAddToFavorites(profileCardMenuTarget)
                  }
                >
                  <Text style={styles.modalButtonTextPrimary}>
                    {favoritedUsernames.has(profileCardMenuTarget.username) ? '💔 Remove from Favorites' : '⭐ Add to Favorites'}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.modalButtonPrimary}
                  onPress={() => handleDirectMessageFromCard(profileCardMenuTarget)}
                >
                  <Text style={styles.modalButtonTextPrimary}>💬 Direct Message</Text>
                </TouchableOpacity>
              </>
            )}

            <TouchableOpacity
              style={styles.modalButtonPrimary}
              onPress={() => handleViewProfileFromCard(profileCardMenuTarget)}
            >
              <Text style={styles.modalButtonTextPrimary}>👁 View Profile</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.modalButtonCancel}
              onPress={() => setProfileCardMenuTarget(null)}
            >
              <Text style={styles.modalButtonTextCancel}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Username Action Modal — appears when a sender's name is tapped in
          a group chat bubble. Offers View Profile (opens main app /profile/:username
          in a new tab) and Direct Message (opens a 1:1 chat with that user). */}
      {usernameModalTarget && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>👤 @{usernameModalTarget}</Text>
            <Text style={styles.modalText}>What would you like to do?</Text>

            <TouchableOpacity
              style={styles.modalButtonPrimary}
              onPress={handleViewProfile}
            >
              <Text style={styles.modalButtonTextPrimary}>👁  View Profile</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.modalButtonPrimary}
              onPress={handleDirectMessage}
            >
              <Text style={styles.modalButtonTextPrimary}>💬  Direct Message</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.modalButtonCancel}
              onPress={() => setUsernameModalTarget(null)}
            >
              <Text style={styles.modalButtonTextCancel}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Public Recipient Modal */}
      {showPublicRecipientModal && (
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>📧 Messaging Non-Members</Text>

            {/* Role-based block banner (only admins/moderators can invite) */}
            {!senderCanInvite && (
              <View style={styles.modalRoleBanner}>
                <Text style={styles.modalRoleBannerText}>
                  🚫 Only administrators or moderators can invite external participants by email.
                </Text>
              </View>
            )}

            <Text style={styles.modalText}>
              This message will be delivered by email to:
            </Text>

            {/* Recipient list with status bubbles per email */}
            {publicRecipients.map((recipient, index) => {
              const check = recipientChecks[recipient.email.toLowerCase()];
              const invCount = check?.invitationCount || 0;
              // Throttle: 2+ existing invitations means the admin should manage
              // the recipient via the main-app invitation queue, not re-invite.
              const isThrottled = invCount >= 2;
              return (
                <View key={index} style={styles.recipientRow}>
                  <Text style={styles.recipientText}>
                    • {recipient.displayName} ({recipient.email})
                  </Text>
                  {/* Status bubbles — only render once the pre-flight has resolved. */}
                  {check && (
                    <View style={styles.bubbleRow}>
                      {check.isMember && (
                        <View style={[styles.statusBubble, styles.bubbleMember]}>
                          <Text style={styles.statusBubbleText}>
                            ✅ Existing member{check.memberUsername ? `: @${check.memberUsername}` : ''}
                          </Text>
                        </View>
                      )}
                      {check.alreadyInConversation && (
                        <View style={[styles.statusBubble, styles.bubbleAlreadySent]}>
                          <Text style={styles.statusBubbleText}>
                            📨 Already in this conversation{check.lastSentAt ? ` · ${formatRelative(check.lastSentAt)}` : ''}
                          </Text>
                        </View>
                      )}
                      {check.hasActiveInvitation && (
                        <View style={[styles.statusBubble, styles.bubbleInvited]}>
                          <Text style={styles.statusBubbleText}>
                            ✉️ Invited{invCount > 1 ? ` · ${invCount}×` : ''}{check.invitationStatus ? ` (${check.invitationStatus})` : ''}{check.invitationSentAt ? ` · ${formatRelative(check.invitationSentAt)}` : ''}
                          </Text>
                        </View>
                      )}
                      {isThrottled && (
                        <View style={[styles.statusBubble, styles.bubbleThrottled]}>
                          <Text style={styles.statusBubbleText}>
                            ⛔ Throttled — manage in invitation queue
                          </Text>
                        </View>
                      )}
                      {!check.isMember && !check.alreadyInConversation && !check.hasActiveInvitation && (
                        <View style={[styles.statusBubble, styles.bubbleNew]}>
                          <Text style={styles.statusBubbleText}>🆕 New recipient</Text>
                        </View>
                      )}
                    </View>
                  )}
                </View>
              );
            })}

            {checkingRecipients && (
              <Text style={styles.checkingText}>Checking recipients…</Text>
            )}

            {/* Throttle banner — shown when ANY recipient has 2+ prior invitations.
                Hard-blocks Send + Invite and routes the admin to the main app's
                invitation queue where they can resend / archive / convert. */}
            {(() => {
              const throttled = publicRecipients.filter((r) => {
                const c = recipientChecks[r.email.toLowerCase()];
                return c && (c.invitationCount || 0) >= 2;
              });
              if (throttled.length === 0) return null;
              return (
                <View style={styles.modalRoleBanner}>
                  <Text style={styles.modalRoleBannerText}>
                    ⛔ {throttled.length === 1
                      ? `${throttled[0].email} has already been invited 2+ times.`
                      : `${throttled.length} recipients have already been invited 2+ times.`}{' '}
                    Please check the Invitation Queue in the main app to resend,
                    archive, or convert the existing invitation.
                  </Text>
                  <TouchableOpacity
                    style={styles.inviteQueueLinkBtn}
                    onPress={() => {
                      openExternalUrl(`${getMainAppUrl()}/invitations`);
                    }}
                  >
                    <Text style={styles.inviteQueueLinkText}>Open Invitation Queue ↗</Text>
                  </TouchableOpacity>
                </View>
              );
            })()}

            <Text style={styles.modalSubtext}>Choose how to send:</Text>

            {/* Send + Invite Button — disabled if:
                  • sender lacks the admin/moderator role, OR
                  • any recipient has 2+ prior invitations (throttle). */}
            {(() => {
              const anyThrottled = publicRecipients.some((r) => {
                const c = recipientChecks[r.email.toLowerCase()];
                return c && (c.invitationCount || 0) >= 2;
              });
              const canSend = senderCanInvite && !anyThrottled;
              return (
                <TouchableOpacity
                  style={[
                    styles.modalButtonPrimary,
                    !canSend && styles.modalButtonDisabled,
                  ]}
                  onPress={() => canSend && sendWithPublicRecipients('both')}
                  disabled={!canSend}
                  activeOpacity={canSend ? 0.7 : 1}
                >
                  <Text style={styles.modalButtonTextPrimary}>
                    ✉️ Send + Invite
                  </Text>
                  <Text style={styles.modalButtonSubtext}>
                    {!senderCanInvite
                      ? 'Requires admin or moderator role'
                      : anyThrottled
                        ? 'Blocked — manage existing invitation in main app'
                        : 'Email includes "Join L3V3L" button'}
                  </Text>
                </TouchableOpacity>
              );
            })()}

            {/* Send Only Button — DISABLED.
                We require every external recipient to be onboarded via the
                "Send + Invite" path so the funnel/lineage stays intact.
                Keeping the button visible (greyed) explains the constraint
                rather than silently removing the option. */}
            <TouchableOpacity
              style={[styles.modalButtonSecondary, styles.modalButtonDisabled]}
              disabled={true}
              activeOpacity={1}
            >
              <Text style={styles.modalButtonTextSecondary}>
                📨 Send Message Only
              </Text>
              <Text style={styles.modalButtonSubtext}>
                Disabled — please use "Send + Invite"
              </Text>
            </TouchableOpacity>

            {/* Cancel Button */}
            <TouchableOpacity
              style={styles.modalButtonCancel}
              onPress={() => {
                setShowPublicRecipientModal(false);
                setPublicRecipients([]);
                setRecipientChecks({});
              }}
            >
              <Text style={styles.modalButtonTextCancel}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0, backgroundColor: colors.bg },
  body: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  backButton: { padding: 3, marginRight: 4 },
  backButtonText: { fontSize: 12, color: colors.accent },
  headerAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    marginRight: 6,
    backgroundColor: colors.border,
  },
  headerAvatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.accent,
  },
  headerAvatarFallbackText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: 'bold',
  },
  headerInfo: { flex: 1, flexDirection: 'column', marginLeft: 4 },
  headerTitleRow: { flexDirection: 'row', alignItems: 'center' },
  headerTitle: { fontSize: 12, fontWeight: 'bold', color: colors.white },
  headerCountBadge: {
    marginLeft: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.textA40,
    backgroundColor: colors.overlaySoft,
    minWidth: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerCountText: {
    color: colors.textSoft,
    fontSize: 8,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  headerMeta: { fontSize: 8, color: colors.textDim, marginTop: 1 },
  headerSubtitle: { fontSize: 9, color: colors.textDim },
  headerSubtitleOnline: { color: colors.success },
  headerStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 2,
  },
  headerStatusInlineDot: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
    backgroundColor: colors.gray500,
    marginRight: 4,
  },
  headerStatusInlineDotOnline: {
    backgroundColor: colors.success,
  },
  // Invited-recipient list (US Vedika @{email} status)
  recipientsList: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.overlaySoft,
  },
  recipientsListTitle: {
    fontSize: 10,
    fontWeight: '700',
    color: colors.textSlate,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  recipientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  recipientEmail: {
    flex: 1,
    fontSize: 12,
    color: colors.textGray300,
    marginRight: 8,
  },
  recipientStatusPill: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: 999,
    borderWidth: 1,
  },
  recipientStatusText: {
    fontSize: 10,
    fontWeight: '700',
  },
  messagesWrapper: {
    flex: 1,
    minHeight: 0,
    flexShrink: 1,
  },
  messagesContainer: {
    flex: 1,
    minHeight: 0,
    flexShrink: 1,
  },
  messagesContent: { padding: 16, flexGrow: 1 },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: { color: colors.textDim, marginTop: 12, fontSize: 14 },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: { color: colors.accent, fontSize: 14, marginBottom: 16, textAlign: 'center' },
  retryBtn: {
    backgroundColor: colors.accentA20,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.accent,
  },
  retryBtnText: { color: colors.accent, fontSize: 14, fontWeight: '600' },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: { color: colors.white, fontSize: 18, marginBottom: 8 },
  emptySubText: { color: colors.textDim, fontSize: 14 },
  messageRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  messageRowOwn: {
    justifyContent: 'flex-end',
  },
  messageBubble: {
    backgroundColor: colors.surface,
    padding: 12,
    borderRadius: 12,
    maxWidth: '75%',
    borderWidth: 1,
    borderColor: colors.border,
  },
  messageBubbleOwn: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
  },
  messageText: {
    color: colors.white,
    fontSize: 15,
    lineHeight: 20,
  },
  messageTextOwn: {
    color: colors.white,
  },
  messageTime: {
    fontSize: 11,
    color: colors.textA60,
    marginTop: 4,
    textAlign: 'right',
  },
  messageReceiptRead: {
    color: colors.info,
    fontWeight: '700',
    marginLeft: 3,
  },
  messageMetaRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  reactionPicker: {
    position: 'absolute',
    top: -20,
    left: 8,
    zIndex: 20,
    elevation: 20,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 3,
  },
  reactionPickerOwn: {
    left: undefined,
    right: 8,
  },
  reactionPickerBtn: {
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  reactionPickerEmoji: {
    fontSize: 17,
  },
  reactionPickerClose: {
    fontSize: 13,
    color: colors.textMuted,
    paddingHorizontal: 2,
  },
  reactionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  reactionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.overlaySoft,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: 'transparent',
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 4,
    marginTop: 3,
  },
  reactionChipMine: {
    backgroundColor: colors.accentA15,
    borderColor: colors.accent,
  },
  reactionChipText: {
    fontSize: 12,
    color: colors.white,
  },
  messageActionBtn: {
    marginLeft: 8,
  },
  messageActionIcon: {
    marginTop: 0,
    fontSize: 8,
    lineHeight: 9,
    textAlign: 'left',
  },
  reconnectBanner: {
    backgroundColor: colors.yellowA15,
    borderColor: colors.yellowA40,
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    marginBottom: 8,
    alignSelf: 'center',
  },
  reconnectBannerText: {
    color: colors.warningAlt,
    fontSize: 12,
    fontWeight: '600',
  },
  senderName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.accent,
    marginBottom: 4,
  },
  systemIntroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 6,
  },
  systemSender: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.accent,
    marginRight: 8,
  },
  systemBadge: {
    backgroundColor: colors.emeraldA12,
    borderWidth: 1,
    borderColor: colors.emeraldA40,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
  },
  systemBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.successAlt,
  },
  publicEmailBadge: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.indigo,
    marginBottom: 4,
    backgroundColor: colors.indigoA10,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  messageBubblePublic: {
    backgroundColor: colors.surfaceAlt,
    borderColor: colors.indigo,
  },
  // Profile-card bubbles get a wider, responsive width so the card content
  // (avatar + sections) has room to breathe and scales with the viewport.
  messageBubbleCard: {
    width: '85%',
    maxWidth: 520,
    padding: 0,
    borderWidth: 0,
    backgroundColor: 'transparent',
  },
  messageBubbleNotif: {
    borderWidth: 1,
    borderColor: colors.accentA50,
    cursor: 'pointer',
  },
  notifBubbleTapHint: {
    color: colors.accent,
    fontSize: 11,
    fontWeight: '600',
    marginTop: 6,
    marginBottom: 2,
  },
  sendErrorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: colors.dangerA12,
    borderWidth: 1,
    borderColor: colors.dangerA40,
  },
  sendErrorText: {
    flex: 1,
    color: colors.dangerSoft,
    fontSize: 13,
    lineHeight: 18,
  },
  sendErrorDismiss: {
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  sendErrorDismissText: {
    color: colors.dangerSoft,
    fontSize: 14,
    fontWeight: '700',
  },
  retentionInfoBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: colors.emeraldA14,
    borderWidth: 1,
    borderColor: colors.emeraldA45,
  },
  retentionInfoText: {
    flex: 1,
    color: colors.successSoft,
    fontSize: 13,
    lineHeight: 18,
  },
  retentionInfoDismiss: {
    marginLeft: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  retentionInfoDismissText: {
    color: colors.successSoft,
    fontSize: 14,
    fontWeight: '700',
  },
  composerLockBanner: {
    marginHorizontal: 16,
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: colors.amberA12,
    borderWidth: 1,
    borderColor: colors.amberA35,
  },
  composerLockBannerText: {
    color: colors.warningSoft,
    fontSize: 13,
    lineHeight: 18,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    flexShrink: 0,
  },
  inputContainerMobile: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  input: {
    flex: 1,
    backgroundColor: colors.bg,
    color: colors.white,
    padding: 12,
    borderRadius: 20,
    maxHeight: 100,
    fontSize: 15,
    borderWidth: 1,
    borderColor: colors.border,
    marginRight: 8,
    height: 44,
  },
  inputMobile: {
    height: 38,
    paddingVertical: 8,
  },
  inputDisabled: {
    opacity: 0.65,
  },
  sendButton: {
    backgroundColor: colors.accent,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 20,
    minWidth: 70,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonMobile: {
    paddingHorizontal: 0,
    minWidth: 36,
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  sendButtonDisabled: {
    backgroundColor: colors.lightText,
  },
  sendButtonText: {
    color: colors.white,
    fontWeight: '600',
    fontSize: 14,
  },
  // Modal styles
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.blackA70,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: colors.surface,
    borderRadius: 16,
    padding: 24,
    width: '85%',
    maxWidth: 400,
    borderWidth: 1,
    borderColor: colors.border,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.white,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalText: {
    fontSize: 14,
    color: colors.lightBorder,
    marginBottom: 12,
    lineHeight: 20,
  },
  recipientText: {
    fontSize: 14,
    color: colors.accent,
    marginLeft: 12,
  },
  recipientRow: {
    marginBottom: 8,
  },
  bubbleRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginLeft: 18,
    marginTop: 4,
  },
  statusBubble: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    marginRight: 6,
    marginBottom: 4,
    borderWidth: 1,
  },
  statusBubbleText: {
    fontSize: 11,
    color: colors.white,
    fontWeight: '600',
  },
  // Green — recipient is already a registered member
  bubbleMember: {
    backgroundColor: colors.successA18,
    borderColor: colors.successA55,
  },
  // Amber — duplicate in this conversation
  bubbleAlreadySent: {
    backgroundColor: colors.warningA18,
    borderColor: colors.warningA55,
  },
  // Blue — invitation already in flight
  bubbleInvited: {
    backgroundColor: colors.infoA18,
    borderColor: colors.infoA55,
  },
  // Neutral — net-new recipient
  bubbleNew: {
    backgroundColor: colors.slateA15,
    borderColor: colors.slateA45,
  },
  // Red — recipient has 2+ prior invitations; admin must use main app queue
  bubbleThrottled: {
    backgroundColor: colors.dangerA18,
    borderColor: colors.dangerA55,
  },
  // Link-style button inside the throttle banner that opens the main app
  // /invitations page in a new tab.
  inviteQueueLinkBtn: {
    marginTop: 8,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.dangerA55,
    alignSelf: 'flex-start',
  },
  inviteQueueLinkText: {
    color: colors.dangerSoft,
    fontSize: 13,
    fontWeight: '600',
  },
  checkingText: {
    fontSize: 12,
    color: colors.textA60,
    marginLeft: 18,
    marginTop: 4,
    fontStyle: 'italic',
  },
  modalRoleBanner: {
    backgroundColor: colors.dangerA15,
    borderColor: colors.dangerA50,
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
    marginTop: 8,
    marginBottom: 12,
  },
  modalRoleBannerText: {
    color: colors.dangerSoft,
    fontSize: 13,
    fontWeight: '600',
  },
  headerInfo: {
    flex: 1,
    flexDirection: 'column',
    marginLeft: 4,
  },
  // Shared tiny header icon button (⏱️ retention, 🗑️ clear chat).
  // Kept small so the chat header stays compact on narrow screens.
  iconButton: {
    paddingVertical: 2,
    paddingHorizontal: 3,
    borderRadius: 4,
    backgroundColor: colors.overlayFaint,
    borderWidth: 1,
    borderColor: colors.overlaySoft,
    marginLeft: 3,
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconButtonText: {
    fontSize: 13,
    lineHeight: 14,
  },
  // Milli header action emojis (clock + trash) — step up from micro.
  headerActionEmoji: {
    fontSize: 11,
    lineHeight: 11,
  },
  iconButtonBadge: {
    fontSize: 8,
    fontWeight: '700',
    color: colors.infoFaint,
    marginLeft: 3,
  },
  modalSubtext: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.white,
    marginTop: 16,
    marginBottom: 12,
  },
  modalButtonPrimary: {
    backgroundColor: colors.successDeep,
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 10,
  },
  modalButtonTextPrimary: {
    color: colors.white,
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  modalButtonSecondary: {
    backgroundColor: colors.indigo,
    padding: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 10,
  },
  modalButtonDisabled: {
    opacity: 0.45,
    backgroundColor: colors.gray600,
  },
  modalButtonTextSecondary: {
    color: colors.white,
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  modalButtonSubtext: {
    color: colors.textA80,
    fontSize: 12,
  },
  modalButtonCancel: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.gray500,
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
  },
  modalButtonTextCancel: {
    color: colors.textGray400,
    fontSize: 14,
    fontWeight: '600',
  },
  // ⚡ Quick Messages button on the composer.
  quickBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    borderWidth: 1,
    borderColor: colors.borderDeep,
  },
  quickBtnMobile: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 6,
  },
  quickBtnText: {
    fontSize: 18,
  },
  quickGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 4,
    marginBottom: 16,
  },
  quickChip: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: colors.indigoA18,
    borderWidth: 1,
    borderColor: colors.indigoA55,
    marginRight: 8,
    marginBottom: 8,
  },
  quickChipDisabled: {
    backgroundColor: colors.slateA10,
    borderColor: colors.slateA35,
    opacity: 0.6,
  },
  quickChipSelected: {
    backgroundColor: colors.emeraldA25,
    borderColor: colors.emeraldA70,
  },
  quickChipText: {
    color: colors.lightBorderSoft,
    fontSize: 14,
    fontWeight: '600',
  },
});

// Profile-card styles. Kept separate from the main `styles` sheet so the card
// is self-contained and easy to extract into its own component later.
const cardStyles = StyleSheet.create({
  card: {
    backgroundColor: colors.accentAlt,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.pinkSoft,
    borderRadius: 24,
    padding: 1,
    marginTop: 4,
    alignSelf: 'stretch',
    width: '100%',
  },
  cardOwn: {
    backgroundColor: colors.accentDark,
    borderColor: colors.dangerBorder,
  },
  cardInner: {
    backgroundColor: colors.surfacePanel,
    borderWidth: 0,
    borderRadius: 18,
    padding: 8,
    position: 'relative',
    overflow: 'hidden',
  },
  cardInnerBgImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    opacity: 0.95,
  },
  cardInnerBgTint: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.scrimA22,
  },
  cardContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroImageWrap: {
    marginBottom: 8,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 12,
  },
  heroOverlay: {
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 6,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    backgroundColor: colors.scrimA40,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  avatar: {
    width: '100%',
    height: 340,
    borderRadius: 12,
    backgroundColor: colors.surfaceNavy,
  },
  heroPhoto: {
    width: '100%',
    height: 355,
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    color: colors.white,
    fontSize: 22,
    fontWeight: '700',
  },
  nameBlock: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 8,
    minWidth: 0,
  },
  nameTouchable: {
    flexShrink: 1,
    maxWidth: '72%',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  heartEmoji: {
    fontSize: 18,
    marginRight: 8,
  },
  menuButton: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginLeft: 6,
    alignSelf: 'flex-start',
    backgroundColor: colors.scrimA85,
    borderRadius: 8,
  },
  menuButtonText: {
    color: colors.white,
    fontSize: 18,
    fontWeight: '300',
    lineHeight: 18,
  },
  name: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '700',
    marginRight: 8,
    flexShrink: 1,
    lineHeight: 24,
  },
  username: {
    color: colors.textGray200,
    fontSize: 14,
    fontWeight: '700',
    flexShrink: 1,
    marginLeft: 4,
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 0,
  },
  pill: {
    backgroundColor: colors.pillBgA62,
    borderWidth: 1,
    borderColor: colors.infoRingA75,
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: 999,
    marginRight: 4,
    marginBottom: 3,
    flexShrink: 1,
    maxWidth: '100%',
  },
  pillText: {
    color: colors.infoPale,
    fontSize: 9,
    fontWeight: '600',
    lineHeight: 14,
  },
  section: {
    marginTop: 7,
    paddingTop: 7,
    paddingHorizontal: 8,
    paddingBottom: 6,
    borderRadius: 10,
    backgroundColor: colors.navyA22,
    borderTopWidth: 1,
    borderTopColor: colors.slateA25,
  },
  educationSection: {
    backgroundColor: colors.scrimA40,
    marginTop: 6,
    paddingTop: 6,
  },
  sectionSubGap: {
    marginTop: 7,
  },
  sectionTitle: {
    color: colors.white,
    fontSize: 10,
    fontWeight: '700',
    marginBottom: 4,
    backgroundColor: colors.pillBorderA55,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 7,
    alignSelf: 'flex-start',
    lineHeight: 14,
  },
  row: {
    marginTop: 3,
  },
  rowTitle: {
    color: colors.infoSoft,
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 14,
  },
  rowText: {
    color: colors.textGray200,
    fontSize: 9,
    marginTop: 2,
    lineHeight: 13,
  },
  message: {
    marginTop: 12,
    color: colors.textBright,
    fontSize: 12,
    lineHeight: 21,
    fontStyle: 'italic',
  },
});
